import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, ArrowRight, Sparkles } from "lucide-react";
import { buildRequestUrl, getCategories, trackHome } from "@/lib/requestContext";

const slugify = (s) => s.toLowerCase().replace(/\s+/g, "-");

function useServiceTypes(kind) {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    let alive = true;
    getCategories().then((all) => {
      if (!alive) return;
      setCats(all.filter((c) => !c.parent_id && c.kind === kind && (!c.status || c.status === "active"))
        .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999) || a.name.localeCompare(b.name)));
    });
    return () => { alive = false; };
  }, [kind]);
  return cats;
}

export function useStartRequest() {
  const navigate = useNavigate();
  return (cat) => {
    if (cat) trackHome("category_select", { category: cat.slug, component: "top_menu" });
    else trackHome("request_click", { component: "top_menu_cta" });
    navigate(buildRequestUrl(cat
      ? { category_id: cat.id, request_source: "homepage_menu", homepage_component: "top_menu" }
      : { request_source: "homepage_menu", homepage_component: "top_menu_cta" }));
  };
}

export function ServiceMenu({ item }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const cats = useServiceTypes(item.display);
  const start = useStartRequest();
  const key = item.display || slugify(item.label);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);
  return (
    <div className="relative" ref={ref} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" onClick={(e) => setOpen((v) => (e.nativeEvent.pointerType === "mouse" ? true : !v))} aria-haspopup="menu" aria-expanded={open} data-testid={`rnav-${key}`}
        className={`inline-flex items-center gap-1 px-3.5 py-2 text-sm font-medium rounded-full transition-colors ${open ? "text-[#071b22] bg-[#165a71]/10" : "text-[#252A34]/80 hover:text-[#071b22] hover:bg-[#f1f5f9]"}`}>
        {item.label}<ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full pt-2 z-50" data-testid={`rnav-dropdown-${key}`}>
          <div role="menu" className="w-[min(640px,90vw)] rounded-3xl border border-slate-200 bg-white shadow-[0_24px_48px_-24px_rgba(7,27,34,0.35)] p-5 animate-in fade-in slide-in-from-top-1 duration-150">
            <div className="flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-[#165a71]">
              <Sparkles className="h-3.5 w-3.5" /> Pick a service to start your request
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-1 max-h-[55vh] overflow-y-auto">
              {cats.map((c) => (
                <button key={c.id} role="menuitem" onClick={() => { setOpen(false); start(c); }} data-testid={`rnav-service-${c.slug}`}
                  className="group text-left rounded-xl px-3 py-2 text-sm text-[#252A34]/85 hover:bg-[#165a71]/5 hover:text-[#071b22] transition-colors">
                  <span className="inline-flex items-center gap-1">{c.name}<ArrowRight className="h-3 w-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform]" /></span>
                </button>
              ))}
            </div>
            {item.to && (
              <Link to={item.to} onClick={() => setOpen(false)} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#165a71] hover:underline" data-testid={`rnav-browse-${key}`}>
                {`Browse all ${item.label.toLowerCase()} services`} <ArrowRight className="h-3 w-3" />
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function MobileServiceMenu({ item, onClose }) {
  const [open, setOpen] = useState(false);
  const cats = useServiceTypes(item.display);
  const start = useStartRequest();
  const key = item.display || slugify(item.label);
  return (
    <div>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid={`mrnav-${key}`}
        className="w-full flex items-center justify-between rounded-xl px-3.5 py-2.5 text-sm font-medium text-[#252A34]/80">
        <span>{item.label}</span><ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pl-3 mt-1 grid grid-cols-2 gap-1 max-h-[45vh] overflow-y-auto" data-testid={`mrnav-children-${key}`}>
          {cats.map((c) => (
            <button key={c.id} onClick={() => { onClose(); start(c); }} data-testid={`mrnav-service-${c.slug}`}
              className="text-left rounded-lg px-3 py-2 text-sm text-[#252A34]/80 hover:bg-[#165a71]/5">{c.name}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export function RequestCta({ item, className = "", testId = "rnav-cta", onDone }) {
  const start = useStartRequest();
  return (
    <button type="button" onClick={() => { onDone?.(); start(null); }} data-testid={testId}
      className={`inline-flex shrink-0 whitespace-nowrap items-center justify-center gap-1.5 rounded-full bg-[#165a71] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#11485a] active:scale-[0.98] transition-[background-color,transform] ${className}`}>
      {item.label}<ArrowRight className="h-4 w-4" />
    </button>
  );
}
